// Generalized agent-eval — admin API client.
// Backend: app/routes/agent_eval.py (POST/GET /api/admin/agent-eval/...)

import api from './api';

export type RunStatus = 'running' | 'completed' | 'failed';
export type JudgeVerdict = 'pass' | 'fail' | 'ambiguous';
export type JudgeConfidence = 'high' | 'medium' | 'low';
export type TriageLabel =
  | 'real_bug'
  | 'judge_wrong'
  | 'sim_drifted_agent_ok'
  | 'other';

export const TRIAGE_LABEL_DISPLAY: Record<TriageLabel, string> = {
  real_bug: 'Real bug',
  judge_wrong: 'Judge wrong',
  sim_drifted_agent_ok: 'Sim drifted, agent fine',
  other: 'Other',
};

export interface RunSummary {
  id: string;
  agent_type: string;
  rubric_name: string;
  status: RunStatus;
  dry_run: boolean;
  case_filter: string | null;
  started_at: string;
  completed_at: string | null;
  pipeline_model_id: string | null;
  agent_temperature: number | null;
  simulator_temperature: number | null;
  summary_total: number;
  summary_flagged: number;
  summary_avg_score: number | null;
  created_at: string;
}

export interface ResultSummary {
  id: string;
  run_id: string;
  case_id: string;
  flagged: boolean;
  avg_score: number | null;
  conversation_turns: number;
  judge_verdict: JudgeVerdict | null;
  judge_confidence: JudgeConfidence | null;
  latency_ms: number;
  error: string | null;
  triage_label: TriageLabel | null;
  triage_labeled_at: string | null;
}

export interface TranscriptTurn {
  role: 'user' | 'agent' | 'tool' | 'system';
  content: string;
  timestamp: string;
  tool_name?: string | null;
  tool_args?: Record<string, unknown> | null;
  tool_result?: unknown;
}

export interface ToolCallRecord {
  tool_name: string;
  args: Record<string, unknown>;
  result: unknown;
  error: string | null;
  duration_ms: number;
}

export interface RubricItemScore {
  name: string;
  score: number;
  justification: string;
}

export interface JudgeJustifications {
  items?: RubricItemScore[];
  summary?: string;
  raw?: unknown;
}

export interface ResultDetail extends ResultSummary {
  transcript: TranscriptTurn[];
  tool_calls: ToolCallRecord[];
  final_db_state: Record<string, unknown> | null;
  judge_justifications: JudgeJustifications | null;
  triage_notes: string | null;
  triage_labeled_by: string | null;
}

export interface RunDetail extends RunSummary {
  results: ResultSummary[];
}

export interface RubricItemAggregate {
  name: string;
  avg_score: number;
  min_score: number;
  max_score: number;
  sample_count: number;
}

export interface RunStats {
  run_id: string;
  total: number;
  flagged: number;
  errored: number;
  avg_score: number | null;
  by_rubric_item: RubricItemAggregate[];
}

export interface RubricInfo {
  name: string;
  intent_family: string;
  description: string;
  items: { name: string; prompt: string }[];
}

const BASE = '/admin/agent-eval';

export const agentEvalApi = {
  listRubrics: async (): Promise<RubricInfo[]> => {
    const r = await api.get<RubricInfo[]>(`${BASE}/rubrics`);
    return r.data;
  },

  listRuns: async (params?: {
    agent_type?: string;
    status?: RunStatus;
    limit?: number;
    offset?: number;
  }): Promise<RunSummary[]> => {
    const r = await api.get<RunSummary[]>(`${BASE}/runs`, { params });
    return r.data;
  },

  getRun: async (runId: string): Promise<RunDetail> => {
    const r = await api.get<RunDetail>(`${BASE}/runs/${runId}`);
    return r.data;
  },

  getRunStats: async (runId: string): Promise<RunStats> => {
    const r = await api.get<RunStats>(`${BASE}/runs/${runId}/stats`);
    return r.data;
  },

  listFlagged: async (params?: {
    agent_type?: string;
    rubric?: string;
    triage?: 'unlabeled' | 'labeled' | 'any';
    limit?: number;
    offset?: number;
  }): Promise<ResultSummary[]> => {
    const r = await api.get<ResultSummary[]>(`${BASE}/results/flagged`, { params });
    return r.data;
  },

  getResult: async (resultId: string): Promise<ResultDetail> => {
    const r = await api.get<ResultDetail>(`${BASE}/results/${resultId}`);
    return r.data;
  },

  applyTriageLabel: async (
    resultId: string,
    label: TriageLabel,
    notes?: string,
  ): Promise<ResultDetail> => {
    const r = await api.post<ResultDetail>(`${BASE}/results/${resultId}/triage`, {
      label,
      notes: notes ?? null,
    });
    return r.data;
  },

  clearTriageLabel: async (resultId: string): Promise<ResultDetail> => {
    const r = await api.delete<ResultDetail>(`${BASE}/results/${resultId}/triage`);
    return r.data;
  },
};
